import { useProductConfigurationVariables } from "../../../providers/ProductConfigurationVariables";
import { useProductOptionContext } from "../ProductOptionContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { FC, useMemo, useState, useEffect } from "react";
import { FeatureForm } from "./FeatureForm";
import { RemoveFeatureDialog } from "./RemoveFeatureDialog";
import { isCompatibleCategory, SubCategoryMap } from "../../../utils/options";
import {
  CategoryData,
  ProductFeature,
  ProductOptionsData,
} from "../ProductOption";
import { IcIcon } from "@shared/components";
import { RemoveIncompatibleCategories } from "./RemoveIncompatibleCategories";
import { GroupedOptionListItem } from "../../../utils/group";
import { categoriesToRemoveToMakeCompatible } from "../../../utils/categories";

type FeaturesTabProps = {
  onAddOption: (category: CategoryData | CategoryData[]) => Promise<void>;
  setShowRemove: (category: string) => void;
};

const FeatureOption: FC<{
  feature: ProductFeature;
  onAddOption: (category: CategoryData | CategoryData[]) => void;
}> = ({ feature, onAddOption }) => {
  const {
    categorys,
    removeCategory,
    catmap,
    selectedCategories,
    groupedOptions,
    incompatibleFeatures,
    featuresAdded,
    categoriesUsed,
    features,
    productConfigurationId,
    productConfigurationQuery,
  } = useProductOptionContext();
  const theme = useTheme();
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const optionalCategorys = useMemo(() => {
    return feature.optionalCategoryIds
      .map((id) => {
        return {
          found: selectedCategories.includes(id),
          id,
        };
      })
      .filter((cat) => isCompatibleCategory(catmap, cat.id, categorys));
  }, [
    JSON.stringify(feature.optionalCategoryIds),
    JSON.stringify(selectedCategories),
    JSON.stringify(categorys),
    JSON.stringify(catmap),
    productConfigurationQuery.data,
  ]);

  const requiredCategorys = useMemo(() => {
    return feature.categoryIds.map((id) => {
      return {
        compatible: isCompatibleCategory(
          catmap,
          id,
          categorys as CategoryData[]
        ),
        input: [catmap, id, categorys],
        categoriesToRemove: categoriesToRemoveToMakeCompatible(
          catmap,
          id,
          categorys as CategoryData[],
          selectedCategories
        ),
        found: selectedCategories.includes(id),
        id,
      };
    });
  }, [
    JSON.stringify(categorys),
    JSON.stringify(feature.categoryIds),
    JSON.stringify(catmap),
    JSON.stringify(selectedCategories),
    productConfigurationQuery.data,
  ]);
  const incompatibleCategorys = useMemo(() => {
    return feature.incompatibleCategoryIds.map((id) => {
      return {
        found: selectedCategories.includes(id),
        id,
      };
    });
  }, [
    JSON.stringify(feature.incompatibleCategoryIds),
    JSON.stringify(selectedCategories),
    productConfigurationQuery.data,
  ]);
  const isCompatible = !incompatibleFeatures.includes(feature._id);
  const isAddable = useMemo(() => {
    const unmovableCategories = categoriesUsed
      .filter((c) => c.optional === false)
      .map((c) => c.categoryId);
    for (const category of requiredCategorys) {
      if (
        category.categoriesToRemove.some((c) => unmovableCategories.includes(c))
      ) {
        return false;
      }
    }
    return true;
  }, [requiredCategorys, optionalCategorys]);
  const isAdded = featuresAdded.includes(feature._id);

  const [showDialog, setShowDialog] = useState(false);

  const handleAddFeature = async (bypassIncompatible?: boolean) => {
    if (!isCompatible && bypassIncompatible !== true) {
      setShowDialog(true);
      return;
    }

    setIsLoading(true);
    const neededCategories = [...requiredCategorys, ...optionalCategorys]
      .filter((c) => !c.found)
      .map((category) => categorys.find((c) => c._id === category.id))
      .filter((c): c is CategoryData => c != null);
    await onAddOption(neededCategories);
    setIsLoading(false);
  };

  const handleRemoveFeature = () => {
    setShowRemoveDialog(true);
  };

  const handleRemoveComplete = async () => {
    setIsLoading(true);
    for (const category of requiredCategorys) {
      if (category.categoriesToRemove) {
        for (const catId of category.categoriesToRemove) {
          const categoryData = categorys.find((c) => c._id === catId);
          if (categoryData) {
            await removeCategory(catId);
          }
        }
      }
    }
    // Wait for the query to update which will reflect the new state
    await productConfigurationQuery.refetch();
    // Now that the state is updated, we can safely add the feature
    await handleAddFeature(true);
  };

  // Add effect to stop loading when features are updated
  useEffect(() => {
    setIsLoading(false);
  }, [featuresAdded]);

  const featureDescription = feature.description?.trim();
  const tooltipTitle = featureDescription ? (
    <Box sx={{ maxWidth: 360, whiteSpace: "pre-line" }}>{featureDescription}</Box>
  ) : "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Typography sx={{ fontSize: 16 }}>{feature.name}</Typography>
      <Tooltip
        title={tooltipTitle}
        disableHoverListener={!featureDescription}
        enterDelay={250}
        arrow
      >
        <span style={{ display: "inline-flex" }}>
          <Button
            variant="contained"
            color={isAdded ? "error" : "primary"}
            onClick={
              isAdded ? handleRemoveFeature : () => handleAddFeature(false)
            }
            disabled={isLoading || !isAddable}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : isAdded ? (
              "Remove"
            ) : (
              "Add"
            )}
          </Button>
        </span>
      </Tooltip>
      <RemoveIncompatibleCategories
        open={showDialog}
        productConfigurationId={productConfigurationId}
        incompatibleCategories={incompatibleCategorys}
        requiredCategories={requiredCategorys}
        categorys={categorys}
        groupedOptions={groupedOptions}
        onClose={() => setShowDialog(false)}
        onRemoveComplete={() => {
          setShowDialog(false);
          handleRemoveComplete();
        }}
      />
      {features.data?.productFeaturesByProduct && showRemoveDialog && (
        <RemoveFeatureDialog
          open={showRemoveDialog}
          productConfigurationId={productConfigurationId}
          featureToRemove={feature}
          allFeatures={
            features.data.productFeaturesByProduct as Array<{
              _id: string;
              name: string;
              categoryIds: string[];
            }>
          }
          featuresAdded={featuresAdded}
          categorys={categorys}
          groupedOptions={groupedOptions}
          onClose={() => setShowRemoveDialog(false)}
          onRemoveComplete={() => {
            // Let the dialog handle its own closing
            productConfigurationQuery.refetch();
          }}
        />
      )}
    </Box>
  );
};

export const FeaturesTab: FC<FeaturesTabProps> = ({
  onAddOption,
  setShowRemove,
}) => {
  const { features } = useProductOptionContext();

  if (!features.data?.productFeaturesByProduct) {
    return null;
  }

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 5,
          }}
        >
          {features.data.productFeaturesByProduct.map((feature) => (
            <FeatureOption
              key={feature._id}
              feature={feature as ProductFeature}
              onAddOption={onAddOption}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
